import { useEffect, useMemo, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';
import type { Job } from '../types';
import { fetchInspectionJobs } from '../lib/apiClient';

// Use API URL for socket connection
const SOCKET_URL = import.meta.env.VITE_API_URL?.replace('/v1', '') ?? import.meta.env.VITE_SOCKET_URL ?? 'https://inspectai-backend-production-1256.up.railway.app';

const normalizeJob = (job: Job): Job => ({
  ...job,
  inspectionId: job.inspectionId,
  roomId: job.roomId,
});

const mergeJob = (existing: Job[], incoming: Job): Job[] => {
  const job = normalizeJob(incoming);
  const index = existing.findIndex((item) => item._id === job._id);
  if (index >= 0) {
    const next = [...existing];
    next[index] = { ...next[index], ...job };
    return next;
  }
  return [job, ...existing];
};

interface ProgressEvent {
  jobId: string;
  progress: number;
  processedUnits?: number;
  totalUnits?: number;
  message?: string;
  currentPhoto?: string;
}

interface PhotoEvent {
  photoId: string;
  roomType?: string;
  confidence?: number;
  issues?: number;
  condition?: string;
  summary?: string;
}

interface InspectionUpdatedEvent {
  inspectionId: string;
  rooms?: Array<{ id: string; name: string; photoCount: number; condition: string }>;
}

export function useInspectionJobs(inspectionId?: string, onInspectionUpdated?: () => void) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentProgress, setCurrentProgress] = useState<ProgressEvent | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['inspection-jobs', inspectionId],
    queryFn: () => fetchInspectionJobs(inspectionId ?? ''),
    enabled: Boolean(inspectionId),
    staleTime: 1000 * 30,
  });

  useEffect(() => {
    if (Array.isArray(data)) {
      setJobs(data);
    }
  }, [data]);

  useEffect(() => {
    if (!inspectionId) {
      return;
    }

    let socket: Socket | undefined;

    try {
      console.log('[Socket] Connecting to:', SOCKET_URL);
      socket = io(`${SOCKET_URL}/inspections`, {
        transports: ['websocket', 'polling'],
        withCredentials: false, // Changed: cross-origin doesn't need credentials for socket
        query: { inspectionId },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });
    } catch (error) {
      console.error('[Socket] Failed to initialize:', error);
      return () => {
        socket?.disconnect();
      };
    }

    socket.on('connect', () => {
      console.log('[Socket] Connected:', socket?.id);
      setIsConnected(true);
    });

    socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error.message);
    });

    // Job lifecycle events
    const handleJobEvent = (job: Job) => {
      console.log('[Socket] Job event:', job);
      setJobs((prev) => mergeJob(prev, job));
    };

    // Progress events from worker
    const handleProgress = (event: ProgressEvent) => {
      console.log('[Socket] Progress:', event);
      setCurrentProgress(event);
      
      // Also update the job in the list
      setJobs((prev) => {
        const index = prev.findIndex((j) => j._id === event.jobId);
        if (index >= 0) {
          const next = [...prev];
          next[index] = { ...next[index], progress: event.progress };
          return next;
        }
        return prev;
      });
    };

    // Photo events
    const handlePhotoAnalyzed = (event: PhotoEvent) => {
      console.log('[Socket] Photo analyzed:', event);
    };

    // Inspection updated - refresh the whole inspection
    const handleInspectionUpdated = (event: InspectionUpdatedEvent) => {
      console.log('[Socket] Inspection updated:', event);
      setCurrentProgress(null);
      
      // Invalidate inspection query to refetch
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      
      // Call the callback if provided
      onInspectionUpdated?.();
    };

    // Job completed
    const handleJobCompleted = (event: { jobId: string; result: unknown; message: string }) => {
      console.log('[Socket] Job completed:', event);
      setCurrentProgress(null);
      
      // Refetch jobs
      refetch();
      
      // Trigger inspection refresh
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      onInspectionUpdated?.();
    };

    socket.on('job.created', handleJobEvent);
    socket.on('job.queued', handleJobEvent);
    socket.on('job.updated', handleJobEvent);
    socket.on('job.processing', handleProgress);
    socket.on('job.progress', handleProgress);
    socket.on('job.completed', handleJobCompleted);
    socket.on('job.failed', handleJobEvent);
    socket.on('photo.classified', handlePhotoAnalyzed);
    socket.on('photo.analyzed', handlePhotoAnalyzed);
    socket.on('inspection.updated', handleInspectionUpdated);

    return () => {
      console.log('[Socket] Disconnecting...');
      socket?.disconnect();
    };
  }, [inspectionId, queryClient, onInspectionUpdated, refetch]);

  const sortedJobs = useMemo(() => {
    return [...jobs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [jobs]);

  // Get the latest active job
  const activeJob = useMemo(() => {
    return sortedJobs.find((j) => j.status === 'processing' || j.status === 'queued');
  }, [sortedJobs]);

  return {
    jobs: sortedJobs,
    activeJob,
    currentProgress,
    isLoading,
    isConnected,
    refetch,
  };
}

